import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Pagination,
  Snackbar,
  Alert,
  Card,
  CardContent,
  InputAdornment,
  IconButton,
  LinearProgress,
  useTheme,
  alpha
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  TrendingUp as TrendingUpIcon,
  School as SchoolIcon,
  Group as GroupIcon,
  AccountBalanceWallet as WalletIcon,
  People as PeopleIcon,
  Code as CodeIcon,
  Paid as PaidIcon
} from '@mui/icons-material';

const BASE_URL = process.env.REACT_APP_BASE_URL;

export default function Payments() {
  const theme = useTheme();
  const [payments, setPayments] = useState([]);
  const [groupClassPayments, setGroupClassPayments] = useState([]);
  const [subscriptionPayments, setSubscriptionPayments] = useState([]);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [activeTab, setActiveTab] = useState('course');
  const [rowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [courseSummary, setCourseSummary] = useState({
    total_amount_received: 0,
    total_platform_earnings: 0,
    total_tutor_earnings: 0,
    total_cohub_earnings: 0,
    total_developer_earnings: 0
  });
  const [groupClassSummary, setGroupClassSummary] = useState({
    total_amount_received: 0,
    total_platform_earnings: 0,
    total_tutor_earnings: 0,
    total_cohub_earnings: 0,
    total_developer_earnings: 0
  });
  const [subscriptionSummary, setSubscriptionSummary] = useState({
    total_amount_received: 0,
    total_subscribers: 0,
    active_subscriptions: 0,
    expired_subscriptions: 0
  });

  const fetchPayments = async () => {
    try {
      const res = await fetch(`${BASE_URL}/api/admin/all-payments`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to fetch payments');
      setPayments(data.data);
      setCourseSummary(data.summary || {});
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  const fetchGroupClassPayments = async () => {
    try {
      const res = await fetch(`${BASE_URL}/api/admin/group-class-payments`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to fetch group class payments');
      setGroupClassPayments(data.data || []);
      setGroupClassSummary(data.summary || {});
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  const fetchSubscriptionPayments = async () => {
    try {
      const res = await fetch(`${BASE_URL}/api/admin/subscription-payments`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to fetch subscription payments');
      setSubscriptionPayments(data.data || []);
      setSubscriptionSummary(data.summary || {});
    } catch (err) {
      console.error('Error fetching subscription payments:', err);
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  const handleSearch = useCallback(
    (query) => {
      const lower = query.toLowerCase();
      let source = payments;
      if (activeTab === 'group-class') source = groupClassPayments;
      else if (activeTab === 'subscription') source = subscriptionPayments;

      const filtered = source.filter(
        (p) =>
          String(p.student_name || p.first_name || '').toLowerCase().includes(lower) ||
          String(p.last_name || '').toLowerCase().includes(lower) ||
          String(p.tutor_name || '').toLowerCase().includes(lower) ||
          String(p.plan_name || p.course_name || '').toLowerCase().includes(lower) ||
          String(p.payment_type || '').toLowerCase().includes(lower) ||
          String(p.reference || '').toLowerCase().includes(lower)
      );
      setFilteredPayments(filtered);
    },
    [payments, groupClassPayments, subscriptionPayments, activeTab]
  );

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await Promise.all([fetchPayments(), fetchGroupClassPayments(), fetchSubscriptionPayments()]);
      setLoading(false);
    };
    fetchData();
  }, []);

  useEffect(() => {
    setPage(1);
    handleSearch(search);
  }, [payments, groupClassPayments, subscriptionPayments, activeTab, search, handleSearch]);

  const handleCloseSnackbar = () => setSnackbar((prev) => ({ ...prev, open: false }));
  const handleClearSearch = () => setSearch('');

  const mergedSummary = {
    total_amount_received:
      Number(courseSummary.total_amount_received || 0) + Number(groupClassSummary.total_amount_received || 0),
    total_platform_earnings:
      Number(courseSummary.total_platform_earnings || 0) + Number(groupClassSummary.total_platform_earnings || 0),
    total_tutor_earnings:
      Number(courseSummary.total_tutor_earnings || 0) + Number(groupClassSummary.total_tutor_earnings || 0),
    total_cohub_earnings:
      Number(courseSummary.total_cohub_earnings || 0) + Number(groupClassSummary.total_cohub_earnings || 0),
    total_developer_earnings:
      Number(courseSummary.total_developer_earnings || 0) + Number(groupClassSummary.total_developer_earnings || 0),
  };

  const activeSummary = activeTab === 'group-class' ? groupClassSummary : (activeTab === 'subscription' ? subscriptionSummary : courseSummary);
  const paginatedData = filteredPayments.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  // Stats cards configuration - Simple design with no gradients
  const statsCards = [
    {
      title: 'Total Amount Received',
      value: mergedSummary.total_amount_received,
      icon: <PaidIcon sx={{ fontSize: 28 }} />,
      color: '#667eea'
    },
    {
      title: 'Platform Earnings',
      value: mergedSummary.total_platform_earnings,
      icon: <TrendingUpIcon sx={{ fontSize: 28 }} />,
      color: '#764ba2'
    },
    {
      title: 'Tutor Earnings',
      value: mergedSummary.total_tutor_earnings,
      icon: <PeopleIcon sx={{ fontSize: 28 }} />,
      color: '#f5576c'
    },
    {
      title: 'Group Class Total',
      value: groupClassSummary.total_amount_received,
      icon: <GroupIcon sx={{ fontSize: 28 }} />,
      color: '#00b4db'
    },
    {
      title: 'Cohub Earnings',
      value: mergedSummary.total_cohub_earnings,
      icon: <WalletIcon sx={{ fontSize: 28 }} />,
      color: '#43e97b'
    },
    {
      title: 'Developer Earnings',
      value: mergedSummary.total_developer_earnings,
      icon: <CodeIcon sx={{ fontSize: 28 }} />,
      color: '#fa709a'
    }
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h4" 
          gutterBottom 
          sx={{ 
            fontWeight: 'bold',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            color: 'transparent',
            mb: 1
          }}
        >
          Payment History
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Manage and track all payment transactions across course enrollments and group classes
        </Typography>
      </Box>

      {/* Stats Cards Grid - Simple Design */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { 
          xs: 'repeat(1, 1fr)',
          sm: 'repeat(2, 1fr)',
          md: 'repeat(3, 1fr)',
          lg: 'repeat(3, 1fr)'
        },
        gap: 2,
        mb: 4
      }}>
        {statsCards.map((card, index) => (
          <Card 
            key={index}
            sx={{ 
              background: '#fff',
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              borderRadius: 2,
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                borderColor: card.color
              }
            }}
          >
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <Box 
                  sx={{ 
                    width: 44,
                    height: 44,
                    borderRadius: 1.5,
                    background: alpha(card.color, 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}
                >
                  <Box sx={{ color: card.color }}>
                    {card.icon}
                  </Box>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      display: 'block',
                      color: 'text.secondary',
                      mb: 0.5,
                      fontSize: '0.75rem',
                      fontWeight: 600
                    }}
                  >
                    {card.title}
                  </Typography>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 'bold',
                      color: card.color,
                      fontSize: { xs: '1rem', sm: '1.1rem' },
                      wordBreak: 'break-word'
                    }}
                  >
                    ₦{card.value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Tabs Section */}
      <Paper 
        elevation={0} 
        sx={{ 
          borderRadius: 3,
          overflow: 'hidden',
          mb: 3,
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
        }}
      >
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2, pt: 1 }}>
          <Tabs
            value={activeTab}
            onChange={(e, value) => setActiveTab(value)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.9rem',
                minHeight: 48,
                '&.Mui-selected': {
                  color: 'primary.main'
                }
              }
            }}
          >
            <Tab 
              value="course" 
              icon={<SchoolIcon sx={{ fontSize: 20 }} />} 
              iconPosition="start"
              label={`Course Enrollment Payments (${payments.length})`} 
            />
            <Tab 
              value="group-class" 
              icon={<GroupIcon sx={{ fontSize: 20 }} />} 
              iconPosition="start"
              label={`Group Class Payments (${groupClassPayments.length})`} 
            />
            <Tab 
              value="subscription" 
              icon={<PaidIcon sx={{ fontSize: 20 }} />} 
              iconPosition="start"
              label={`Subscription Payments (${subscriptionPayments.length})`} 
            />
          </Tabs>
        </Box>

        {/* Active Tab Info */}
        <Box sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.02), borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Viewing:
              </Typography>
              <Chip
                size="small"
                color={activeTab === 'course' ? 'primary' : 'success'}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {activeTab === 'course' ? '₦' : '₦'}
                    {Number(activeSummary.total_amount_received || 0).toLocaleString()}
                  </Box>
                }
                variant="outlined"
              />
            </Box>
            <Chip
              size="small"
              variant="filled"
              color={activeTab === 'course' ? 'primary' : 'success'}
              label={`${activeTab === 'course' ? 'Course Enrollment' : 'Group Class'} Tab Active`}
            />
          </Box>
        </Box>

        {/* Search Bar */}
        <Box sx={{ p: 2 }}>
          <TextField
            fullWidth
            placeholder="Search by student, tutor, course, payment type, or reference number..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: search && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleClearSearch}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: 'white'
              }
            }}
          />
        </Box>
      </Paper>

      {/* Payment Table */}
      <Paper 
        elevation={0} 
        sx={{ 
          borderRadius: 3,
          overflow: 'hidden',
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
        }}
      >
        {loading ? (
          <Box sx={{ p: 4 }}>
            <LinearProgress />
          </Box>
        ) : (
          <>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    {activeTab === 'subscription' ? (
                      <>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#fafafa' }}>Student Name</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#fafafa' }}>Email</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#fafafa' }}>Subscription Plan</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#fafafa' }}>Payment Status</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#fafafa' }} align="right">Amount</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#fafafa' }}>Transaction Date</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#fafafa' }}>Reference</TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#fafafa' }}>Student</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#fafafa' }}>Tutor</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#fafafa' }}>Course</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#fafafa' }}>Type</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#fafafa' }}>Plan</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#fafafa' }} align="right">Total Amount</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#fafafa' }} align="right">Platform Earn</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#fafafa' }} align="right">Tutor Earn</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#fafafa' }}>Reference</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#fafafa' }}>Date Paid</TableCell>
                      </>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={activeTab === 'subscription' ? 7 : 10} align="center" sx={{ py: 8 }}>
                        <Typography variant="body1" color="text.secondary">
                          No payment records found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : activeTab === 'subscription' ? (
                    // Subscription payments table
                    paginatedData.map((payment) => (
                      <TableRow key={payment.id} hover>
                        <TableCell>{payment.name || 'N/A'}</TableCell>
                        <TableCell>{payment.email || 'N/A'}</TableCell>
                        <TableCell>{payment.plan_name || 'N/A'}</TableCell>
                        <TableCell>
                          <Chip 
                            size="small"
                            label={payment.status?.toUpperCase() || 'ACTIVE'}
                            color={payment.status === 'active' ? 'success' : payment.status === 'expired' ? 'warning' : 'error'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontWeight="600">
                            ₦{Number(payment.amount_paid || 0).toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {payment.created_at ? new Date(payment.created_at).toLocaleDateString('en-GB') : 'N/A'}
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" fontFamily="monospace">
                            {payment.payment_reference || 'N/A'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    // Course & Group class payments table
                    paginatedData.map((payment) => (
                      <TableRow key={payment.id} hover>
                        <TableCell>{payment.student_name || 'N/A'}</TableCell>
                        <TableCell>{payment.tutor_name || 'N/A'}</TableCell>
                        <TableCell>{payment.course_name || 'N/A'}</TableCell>
                        <TableCell>
                          <Chip 
                            size="small"
                            label={String(payment.payment_type || (activeTab === 'group-class' ? 'group_class' : 'course')).replace('_', ' ')}
                            color={activeTab === 'group-class' ? 'success' : 'primary'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            size="small"
                            label={payment.payment_plan || 'N/A'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontWeight="600">
                            ₦{Number(payment.amount).toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          ₦{Number(
                            activeTab === 'group-class'
                              ? payment.platform_earning
                              : payment.platform_profit
                          ).toLocaleString()}
                        </TableCell>
                        <TableCell align="right">
                          ₦{Number(payment.tutor_earning).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" fontFamily="monospace">
                            {payment.reference || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {payment.paid_at ? new Date(payment.paid_at).toLocaleDateString('en-GB') : 'N/A'}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            {filteredPayments.length > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3, borderTop: 1, borderColor: 'divider' }}>
                <Pagination
                  count={Math.ceil(filteredPayments.length / rowsPerPage)}
                  page={page}
                  onChange={(e, newPage) => setPage(newPage)}
                  color="primary"
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}
          </>
        )}
      </Paper>

      {/* Snackbar Notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          variant="filled"
          sx={{ width: '100%', borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}